import { cn } from '@mds/shared/lib/utils';

export type AnswerPanelItem = {
  /** The question, repeated small over its answer. */
  question: string;
  /** The display line. */
  answer: string;
  /** The large faint word behind the copy — "Do". Decoration; never announced. */
  word: string;
};

export type AnswerPanelProps = {
  item: AnswerPanelItem;
  /** Which answer this is, from 0 — drives the bars. */
  index: number;
  count: number;
  /** The id the tabs point at with `aria-controls`. */
  id: string;
  className?: string;
};

/**
 * The dark panel that answers whichever question is chosen beside it: the
 * question small, the answer as a display line, a faint word behind it all,
 * and a bar per question along the foot.
 *
 * The other half of a selectable `RuledList`. It holds no state — the page owns
 * the index and hands the same number to both, so the tab and the panel cannot
 * disagree about which question is open.
 *
 * `data-ground="panel"` so anything written against the text roles inside it
 * reads on the charcoal without an override.
 */
export default function AnswerPanel({ item, index, count, id, className }: AnswerPanelProps) {
  return (
    <div
      id={id}
      role="tabpanel"
      aria-label={item.question}
      data-ground="panel"
      className={cn(
        'relative isolate flex min-h-96 flex-col overflow-hidden rounded-card bg-panel p-block text-on-panel',
        className,
      )}
    >
      {/* The dim mint of the brand wash, which on the charcoal is just enough
          to be seen as a shape and nowhere near enough to be read as a word —
          which is the point. Behind the copy, and clipped by the corner. */}
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute -right-4 -bottom-12 -z-10 select-none',
          'font-display text-ghost text-brand-wash uppercase',
        )}
      >
        {item.word}
      </span>

      {/* Keyed on the index so the block remounts and rises in again each
          time the answer changes, rather than swapping its words in place. */}
      <div key={index} className="animate-reveal-up motion-reduce:animate-none">
        <p className="text-lg font-medium text-on-panel/80">{item.question}</p>
        <p className="mt-4 text-2xl leading-snug text-balance lg:text-3xl">{item.answer}</p>
      </div>

      {/* `mt-auto` holds the bars at the foot whatever the answer's length, so
          switching between a short answer and a long one does not move them. */}
      <div aria-hidden="true" className="mt-auto flex gap-2 pt-block">
        {Array.from({ length: count }, (_, i) => (
          <span
            key={i}
            className={cn(
              'h-1 flex-1 rounded-full transition-colors duration-(--duration-base) ease-standard',
              i === index ? 'bg-brand' : 'bg-on-panel/20',
            )}
          />
        ))}
      </div>
    </div>
  );
}
